// Production-safe logging utility with timestamps and deep redaction.
type LogLevel = 'info' | 'warn' | 'error' | 'debug'

interface LogData {
  message: string
  error?: unknown
  context?: Record<string, unknown>
  userId?: string
  requestId?: string
}

const REDACTED = '[REDACTED]'
const REDACTED_AUTH_SESSION = '[REDACTED_AUTH_SESSION]'
const MAX_LOG_DEPTH = 6
const MAX_OBJECT_KEYS = 100

const SENSITIVE_KEY_PATTERN =
  /(?:password|passwd|passcode|token|secret|api[_-]?key|authorization|cookie|session|private[_-]?key)/i
const PERSONAL_DATA_KEYS = new Set([
  'email',
  'phone',
  'phone_number',
  'full_name',
  'first_name',
  'last_name',
  'avatar_url',
  'user_metadata',
  'identities',
])

function redactTextPatterns(value: string): string {
  return value
    .replace(/\b(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi, `$1${REDACTED}`)
    .replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, REDACTED)
    .replace(/\b([A-Z0-9._%+-]+)@([A-Z0-9.-]+\.[A-Z]{2,})\b/gi, REDACTED)
    .replace(/\b(cookie|set-cookie)\s*:\s*[^\r\n]*/gi, `$1: ${REDACTED}`)
    .replace(
      /((?:password|passwd|passcode|access[_-]?token|refresh[_-]?token|provider[_-]?(?:refresh[_-]?)?token|id[_-]?token|api[_-]?key|authorization|secret)\s*["']?\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;}]+)/gi,
      `$1${REDACTED}`,
    )
}

/**
 * Remove credentials and common personal data from unstructured log text.
 *
 * Auth client failures can stringify a complete session after a short error
 * prefix. If a session token key is present, discard the complete payload tail
 * instead of trying to identify every nested field that it can contain.
 */
export function redactSensitiveText(value: string): string {
  const authPayloadIndex = value.search(
    /["']?(?:access_token|refresh_token|provider_token|provider_refresh_token)["']?\s*[:=]/i,
  )

  if (authPayloadIndex >= 0) {
    const prefix = value
      .slice(0, authPayloadIndex)
      .replace(/[\s{[(,:='"]+$/g, '')
      .trimEnd()
    return prefix
      ? `${redactTextPatterns(prefix)} ${REDACTED_AUTH_SESSION}`
      : REDACTED_AUTH_SESSION
  }

  return redactTextPatterns(value)
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/-/g, '_')
  return SENSITIVE_KEY_PATTERN.test(normalized) || PERSONAL_DATA_KEYS.has(normalized)
}

function sanitizeStringValue(value: string): string {
  const redacted = redactSensitiveText(value)
  if (/^(?:cus_|pi_|sub_|sk_|pk_)/.test(redacted)) {
    return `${redacted.slice(0, 8)}...`
  }
  return redacted
}

function sanitizeValue(
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): unknown {
  if (typeof value === 'string') return sanitizeStringValue(value)
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value.toString()
  if (typeof value === 'symbol') return value.toString()
  if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`

  if (depth >= MAX_LOG_DEPTH) return '[MaxDepth]'
  if (typeof value !== 'object') return redactSensitiveText(String(value))
  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  if (value instanceof Date) return value.toISOString()

  if (value instanceof Error) {
    const sanitized: Record<string, unknown> = {
      name: redactSensitiveText(value.name),
      message: redactSensitiveText(value.message),
    }
    if (value.stack) sanitized.stack = redactSensitiveText(value.stack)

    for (const [key, entry] of Object.entries(value).slice(0, MAX_OBJECT_KEYS)) {
      sanitized[key] = isSensitiveKey(key)
        ? REDACTED
        : sanitizeValue(entry, seen, depth + 1)
    }
    return sanitized
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_OBJECT_KEYS).map((entry) => sanitizeValue(entry, seen, depth + 1))
  }

  const sanitized: Record<string, unknown> = {}
  let entries: Array<[string, unknown]>
  try {
    entries = Object.entries(value as Record<string, unknown>).slice(0, MAX_OBJECT_KEYS)
  } catch {
    return '[Unserializable]'
  }

  for (const [key, entry] of entries) {
    sanitized[key] = isSensitiveKey(key)
      ? REDACTED
      : sanitizeValue(entry, seen, depth + 1)
  }
  return sanitized
}

/** Deeply sanitize a value before it reaches any server console method. */
export function sanitizeLogValue(value: unknown): unknown {
  return sanitizeValue(value, new WeakSet<object>(), 0)
}

// Helper to format timestamp for console output.
function getTimestamp(): string {
  const now = new Date()
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  const ms = String(now.getMilliseconds()).padStart(3, '0')
  return `${hours}:${minutes}:${seconds}.${ms}`
}

// Store original console methods before any wrapping. Logger calls use these
// methods to avoid duplicate timestamps.
const originalConsoleLog = console.log.bind(console)
const originalConsoleError = console.error.bind(console)
const originalConsoleWarn = console.warn.bind(console)
const originalConsoleInfo = console.info.bind(console)

function alreadyTimestamped(value: unknown, level?: LogLevel): boolean {
  const firstArg = String(value ?? '')
  if (/^\[\d{2}:\d{2}:\d{2}\.\d{3}\]/.test(firstArg)) return true
  if (!level) return false
  return firstArg.includes(`[${level.toUpperCase()}]`)
}

function writeSanitized(
  writer: (...args: unknown[]) => void,
  args: unknown[],
  level?: LogLevel,
) {
  const sanitizedArgs = args.map(sanitizeLogValue)
  if (alreadyTimestamped(sanitizedArgs[0], level)) {
    writer(...sanitizedArgs)
  } else {
    writer(`[${getTimestamp()}]`, ...sanitizedArgs)
  }
}

// Wrap direct server console calls so one missed logger migration cannot emit a
// raw auth response or request payload.
let consoleWrapped = false
function setupConsoleTimestamps() {
  if (typeof window !== 'undefined' || consoleWrapped) return

  console.log = (...args: unknown[]) => writeSanitized(originalConsoleLog, args)
  console.error = (...args: unknown[]) => writeSanitized(originalConsoleError, args, 'error')
  console.warn = (...args: unknown[]) => writeSanitized(originalConsoleWarn, args, 'warn')
  console.info = (...args: unknown[]) => writeSanitized(originalConsoleInfo, args, 'info')
  consoleWrapped = true
}

if (typeof window === 'undefined') setupConsoleTimestamps()

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development'
  private logLevel: LogLevel =
    (process.env.LOG_LEVEL as LogLevel) || (this.isDevelopment ? 'debug' : 'info')

  private sanitizeError(error: unknown): Record<string, unknown> | null {
    if (!error) return null

    const sanitized = sanitizeLogValue(error)
    if (sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)) {
      const record = sanitized as Record<string, unknown>
      if (!this.isDevelopment) delete record.stack
      return record
    }

    return { message: redactSensitiveText(String(sanitized)) }
  }

  private sanitizeContext(context: Record<string, unknown>): Record<string, unknown> {
    const sanitized = sanitizeLogValue(context)
    return sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)
      ? sanitized as Record<string, unknown>
      : {}
  }

  private formatMessage(level: LogLevel, data: LogData): string {
    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`
    let message = `${prefix} ${redactSensitiveText(data.message)}`

    if (data.requestId) message += ` [Request: ${redactSensitiveText(data.requestId)}]`
    if (data.userId) message += ` [User: ${redactSensitiveText(data.userId)}]`

    if (data.context && Object.keys(data.context).length > 0) {
      try {
        message += ` ${JSON.stringify(data.context)}`
      } catch {
        // The sanitizer handles cycles. Keep logging the base message if an
        // unusual host object still cannot be serialized.
      }
    }

    return redactSensitiveText(message)
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    }
    return levels[level] >= levels[this.logLevel]
  }

  info(message: string, context?: Record<string, unknown>) {
    if (!this.shouldLog('info')) return
    const logData: LogData = { message, context: this.sanitizeContext(context || {}) }
    originalConsoleLog(this.formatMessage('info', logData))
  }

  warn(message: string, context?: Record<string, unknown>) {
    if (!this.shouldLog('warn')) return
    const logData: LogData = { message, context: this.sanitizeContext(context || {}) }
    originalConsoleWarn(this.formatMessage('warn', logData))
  }

  error(message: string, error?: unknown, context?: Record<string, unknown>) {
    const logData: LogData = {
      message,
      error: this.sanitizeError(error),
      context: this.sanitizeContext(context || {}),
    }

    const base = this.formatMessage('error', logData)
    const errObj = logData.error ? ` ${JSON.stringify(logData.error)}` : ''
    originalConsoleError(base + errObj)
  }

  debug(message: string, context?: Record<string, unknown>) {
    if (!this.shouldLog('debug')) return
    const logData: LogData = { message, context: this.sanitizeContext(context || {}) }
    originalConsoleLog(this.formatMessage('debug', logData))
  }

  apiError(endpoint: string, error: unknown, requestId?: string) {
    this.error(`API Error in ${endpoint}`, error, {
      endpoint,
      method: 'POST',
      requestId,
    })
  }

  stripeError(operation: string, error: unknown, customerId?: string) {
    this.error(`Stripe Error in ${operation}`, error, {
      operation,
      customerId: customerId ? `${customerId.substring(0, 8)}...` : undefined,
    })
  }
}

export const logger = new Logger()
